class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

/* ============================================================
   TATTERED TROLL - site-wide behaviours

   Everything below this line is custom and unrelated to details
   disclosure. It lives here because layout/theme.liquid already
   loads this file on every page, which avoids rewriting the
   23KB sections/header.liquid. If that file ever gets edited
   directly, move these blocks there and delete them here.

     1. Sticky header scroll smoothing
     2. Loot rain easter egg
   ============================================================ */

/* ------------------------------------------------------------
   1. STICKY HEADER SCROLL SMOOTHING

   Dawn's <sticky-header> binds its scroll handler with
   addEventListener('scroll', fn, false) - non-passive and
   unthrottled. Non-passive means the browser must wait for the
   handler before it can scroll, and unthrottled means it runs
   several times per frame doing classList work.

   This re-binds the same handler as passive and coalesces it to
   one call per animation frame. It does not change what the
   handler does. If it can't find the element it does nothing
   and Dawn's original binding stays in place.
   ------------------------------------------------------------ */
(function () {
  var attempts = 0;

  function smoothStickyHeaderScroll() {
    var stickyHeader = document.querySelector('sticky-header');
    if (!stickyHeader || typeof stickyHeader.onScrollHandler !== 'function') return false;
    if (stickyHeader.ttScrollPatched) return true;

    var original = stickyHeader.onScrollHandler;
    var ticking = false;

    var throttled = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        original();
      });
    };

    window.removeEventListener('scroll', original, false);
    window.addEventListener('scroll', throttled, { passive: true });

    // Keep the reference in sync so disconnectedCallback still cleans up.
    stickyHeader.onScrollHandler = throttled;
    stickyHeader.ttScrollPatched = true;

    return true;
  }

  var poll = setInterval(function () {
    attempts += 1;
    if (smoothStickyHeaderScroll() || attempts > 60) clearInterval(poll);
  }, 50);

  smoothStickyHeaderScroll();
})();

/* ------------------------------------------------------------
   2. LOOT RAIN EASTER EGG

   Scrub the page up and down on a phone and it rains coins,
   gems, swords, axes and the odd treasure chest. The loot can
   be grabbed, dragged and flung.

   TRIGGER: four direction reversals inside 2.5s, where each
   swing travelled at least 60px. The swing distance is the
   important part - without it a twitchy finger during momentum
   scrolling fires the egg constantly. Reading a product page
   does not produce four qualifying reversals that fast.

   HOW MANY TIMES: set MAX_PLAYS and LIMIT_SCOPE below.
     MAX_PLAYS   0 means unlimited, otherwise runs allowed
                 within the scope.
     LIMIT_SCOPE 'page'    resets on every page load
                 'session' whole visit, resets on tab close
                 'forever' per device until site data cleared

   WHAT FALLS: LOOT_MIX is a weighted table. Weights are
   relative, they do not need to total 100. Set one to 0 to
   remove that item. Gem colours are in GEM_COLORS - ruby is
   listed twice so it shows up about twice as often as the
   other stones.

   INTERACTION - the important bit:
   The canvas stays pointer-events: none so it can never swallow
   a tap on the cart or a CTA. Instead the touch handlers sit on
   window and hit-test against the loot themselves. A touch is
   only intercepted (preventDefault) when it actually lands on a
   piece - touch empty space and the page scrolls as normal.
   That is why these listeners are non-passive: preventDefault
   is the whole mechanism, and a passive listener cannot call
   it. They are added when the rain starts and torn down the
   moment it ends, so there is no standing scroll cost.

   A grabbed piece swells to GRAB_SCALE so it can actually be
   seen, eases rather than snaps, and is drawn above everything
   else. Dragging it through others shoves them aside, and
   releasing mid-swipe flings it with the finger's velocity.
   Multi-touch works - grab several at once.

   TESTING: on desktop add ?loot=1 to any URL to bypass the
   mobile gate, or call window.ttLootRain() from the console.
   Mouse drag works for grabbing too.
   window.ttLootRain.reset() clears the play count.
   window.ttLootRain.plays() reports it.
   ------------------------------------------------------------ */
(function () {
  var MAX_PLAYS = 10; // TUNE: 0 = unlimited
  var LIMIT_SCOPE = 'session'; // TUNE: 'page' | 'session' | 'forever'

  var NEEDED_REVERSALS = 4; // TUNE: flicks required
  var WINDOW_MS = 2500; // TUNE: they must all land inside this
  var MIN_SWING = 60; // TUNE: px a swing must travel to count
  var COOLDOWN_MS = 5000; // TUNE: quiet period after a run

  var GRAB_PAD = 26; // TUNE: min grab radius in px, for fat fingers
  var GRAB_SCALE = 2.4; // TUNE: how much a held piece swells
  var SCALE_EASE = 0.22; // TUNE: 0-1, higher snaps faster
  var FLING_MAX = 38; // TUNE: px per frame cap on a thrown piece
  var SHOVE_RADIUS = 64; // TUNE: how close a drag knocks other loot

  // TUNE: relative weights, any scale. 0 removes an item.
  var LOOT_MIX = [
    ['gem', 32],
    ['coin', 30],
    ['sword', 14],
    ['axe', 12],
    ['chest', 8]
  ];

  // TUNE: [face colour, shadow facet colour]
  var GEM_COLORS = [
    ['#e8394f', '#8c0f22'], // ruby
    ['#e8394f', '#8c0f22'], // ruby again - weights it x2
    ['#3fd18a', '#137a4b'], // emerald
    ['#5aa9f5', '#1d4fa8'], // sapphire
    ['#b98cff', '#6a35c9'], // amethyst
    ['#ffc94d', '#c07a12'], // topaz
    ['#dff3ff', '#9cc2d8'] // diamond
  ];

  var STORE_KEY = 'tt_loot_plays';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var forced = /[?&]loot=1/.test(window.location.search);
  var lastY = window.pageYOffset;
  var dir = 0;
  var swingStartY = lastY;
  var reversals = [];
  var lastRun = 0;
  var running = false;
  var pagePlays = 0;

  var MIX_TOTAL = 0;
  for (var m = 0; m < LOOT_MIX.length; m++) MIX_TOTAL += LOOT_MIX[m][1];

  function pickType() {
    var roll = Math.random() * MIX_TOTAL;
    for (var i = 0; i < LOOT_MIX.length; i++) {
      roll -= LOOT_MIX[i][1];
      if (roll <= 0) return LOOT_MIX[i][0];
    }
    return LOOT_MIX[0][0];
  }

  // Safari in private mode throws on storage access rather than
  // returning null, so every touch of it is guarded. On failure we
  // silently fall back to counting per page load.
  function store() {
    try {
      if (LIMIT_SCOPE === 'session') return window.sessionStorage;
      if (LIMIT_SCOPE === 'forever') return window.localStorage;
    } catch (e) {
      return null;
    }
    return null;
  }

  function getPlays() {
    var s = store();
    if (!s) return pagePlays;
    try {
      return parseInt(s.getItem(STORE_KEY), 10) || 0;
    } catch (e) {
      return pagePlays;
    }
  }

  function bumpPlays() {
    var next = getPlays() + 1;
    pagePlays += 1;
    var s = store();
    if (!s) return;
    try {
      s.setItem(STORE_KEY, String(next));
    } catch (e) {
      /* quota or private mode - page count still applies */
    }
  }

  function playsLeft() {
    if (MAX_PLAYS === 0) return Infinity;
    return MAX_PLAYS - getPlays();
  }

  function isMobile() {
    return (
      window.matchMedia('(max-width: 749px)').matches ||
      window.matchMedia('(pointer: coarse)').matches
    );
  }

  function onScroll() {
    // Once they are out of plays there is nothing left to detect,
    // so stop doing work on every scroll event.
    if (playsLeft() <= 0) {
      window.removeEventListener('scroll', onScroll);
      return;
    }

    var y = window.pageYOffset;
    var delta = y - lastY;
    lastY = y;
    if (Math.abs(delta) < 6) return; // ignore drift and rubber-band settle

    var nextDir = delta > 0 ? 1 : -1;

    if (dir === 0) {
      dir = nextDir;
      swingStartY = y;
      return;
    }
    if (nextDir === dir) return;

    // Direction just flipped. Only count it if the swing that ended
    // here actually covered ground.
    if (Math.abs(y - swingStartY) >= MIN_SWING) {
      var now = Date.now();
      reversals.push(now);
      reversals = reversals.filter(function (t) {
        return now - t <= WINDOW_MS;
      });

      if (
        reversals.length >= NEEDED_REVERSALS &&
        !running &&
        now - lastRun > COOLDOWN_MS &&
        (forced || isMobile())
      ) {
        reversals.length = 0;
        lootRain();
      }
    }

    dir = nextDir;
    swingStartY = y;
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  function lootRain() {
    if (running) return;
    if (playsLeft() <= 0) return;
    running = true;
    lastRun = Date.now();
    bumpPlays();

    if (navigator.vibrate) {
      try {
        navigator.vibrate(12);
      } catch (e) {
        /* some browsers throw on gesture-less vibrate */
      }
    }

    var canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText =
      'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9998;';
    document.body.appendChild(canvas);

    var ctx = canvas.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = 0;
    var h = 0;

    function sizeCanvas() {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    sizeCanvas();
    window.addEventListener('resize', sizeCanvas);

    var count = Math.round(Math.min(64, Math.max(26, w / 8)));
    var items = [];

    for (var i = 0; i < count; i++) {
      var type = pickType();
      var size;
      var spin;

      if (type === 'sword') {
        size = 34 + Math.random() * 16;
        spin = 0.05; // heavy, tumbles lazily
      } else if (type === 'axe') {
        size = 28 + Math.random() * 14;
        spin = 0.07;
      } else if (type === 'chest') {
        size = 20 + Math.random() * 10;
        spin = 0.05;
      } else if (type === 'coin') {
        size = 11 + Math.random() * 8;
        spin = 0.16; // coins flash edge-on fastest
      } else {
        size = 11 + Math.random() * 8;
        spin = 0.11;
      }

      items.push({
        type: type,
        size: size,
        x: Math.random() * w,
        // staggered well above the fold so it arrives as a shower,
        // not one solid wall of loot
        y: -30 - Math.random() * h * 0.85 - i * 5,
        // heavier pieces fall a touch faster
        vy: (type === 'sword' || type === 'axe' || type === 'chest' ? 2.1 : 1.7) + Math.random() * 2.2,
        vx: (Math.random() - 0.5) * 0.7,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * spin * 2,
        sway: Math.random() * Math.PI * 2,
        // big steel sways less than a coin caught in the air
        swaySpeed: (type === 'sword' || type === 'axe' ? 0.012 : 0.02) + Math.random() * 0.028,
        swayAmt: type === 'sword' || type === 'axe' ? 0.35 : 0.7,
        gem: GEM_COLORS[Math.floor(Math.random() * GEM_COLORS.length)],
        cut: Math.random() < 0.45 ? 'round' : 'kite',
        held: false,
        scale: 1
      });
    }

    /* ---------- grabbing ---------- */

    var pointers = {}; // active drags, keyed by touch identifier or 'mouse'

    function grabRadius(item) {
      var r = item.type === 'sword' || item.type === 'axe' ? item.size * 0.5 : item.size * 0.7;
      return Math.max(r, GRAB_PAD);
    }

    function hitTest(x, y) {
      // walk backwards: later items draw on top, so they win the grab
      for (var i = items.length - 1; i >= 0; i--) {
        var item = items[i];
        if (item.held) continue;
        if (item.y < -40 || item.y > h + 60) continue;
        var dx = x - item.x;
        var dy = y - item.y;
        var r = grabRadius(item);
        if (dx * dx + dy * dy <= r * r) return item;
      }
      return null;
    }

    function shoveNearby(x, y, vx, vy, exclude) {
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (item === exclude || item.held) continue;
        var dx = item.x - x;
        var dy = item.y - y;
        var d2 = dx * dx + dy * dy;
        if (d2 > SHOVE_RADIUS * SHOVE_RADIUS) continue;
        var d = Math.sqrt(d2) || 1;
        var falloff = 1 - d / SHOVE_RADIUS;
        item.vx += (vx * 0.35 + (dx / d) * 2.2) * falloff;
        item.vy += (vy * 0.35 + (dy / d) * 1.4) * falloff;
        item.vr += (Math.random() - 0.5) * 0.25 * falloff;
      }
    }

    function startDrag(id, x, y) {
      var item = hitTest(x, y);
      if (!item) return false;
      item.held = true;
      item.vx = 0;
      item.vy = 0;
      pointers[id] = {
        item: item,
        offX: item.x - x,
        offY: item.y - y,
        lastX: x,
        lastY: y,
        lastT: Date.now(),
        vx: 0,
        vy: 0
      };
      if (navigator.vibrate) {
        try {
          navigator.vibrate(8);
        } catch (e) {
          /* no-op */
        }
      }
      return true;
    }

    function moveDrag(id, x, y) {
      var p = pointers[id];
      if (!p) return false;
      var now = Date.now();
      var dt = Math.max(now - p.lastT, 8); // guard against divide-by-zero
      // px per ms -> px per frame, smoothed so one jittery sample
      // can't produce a wild fling
      var nvx = ((x - p.lastX) / dt) * 16;
      var nvy = ((y - p.lastY) / dt) * 16;
      p.vx = p.vx * 0.6 + nvx * 0.4;
      p.vy = p.vy * 0.6 + nvy * 0.4;
      p.lastX = x;
      p.lastY = y;
      p.lastT = now;

      p.item.x = x + p.offX;
      p.item.y = y + p.offY;
      p.item.rot += nvx * 0.008;

      shoveNearby(x, y, p.vx, p.vy, p.item);
      return true;
    }

    function endDrag(id) {
      var p = pointers[id];
      if (!p) return;
      p.item.held = false;
      p.item.vx = Math.max(-FLING_MAX, Math.min(FLING_MAX, p.vx));
      p.item.vy = Math.max(-FLING_MAX, Math.min(FLING_MAX, p.vy));
      p.item.vr += p.vx * 0.01;
      delete pointers[id];
    }

    function onTouchStart(e) {
      var grabbedAny = false;
      for (var i = 0; i < e.changedTouches.length; i++) {
        var t = e.changedTouches[i];
        if (startDrag(t.identifier, t.clientX, t.clientY)) grabbedAny = true;
      }
      // Only swallow the gesture if a finger actually landed on loot.
      // Otherwise the page scrolls exactly as it normally would.
      if (grabbedAny && e.cancelable) e.preventDefault();
    }

    function onTouchMove(e) {
      var movedAny = false;
      for (var i = 0; i < e.changedTouches.length; i++) {
        var t = e.changedTouches[i];
        if (moveDrag(t.identifier, t.clientX, t.clientY)) movedAny = true;
      }
      if (movedAny && e.cancelable) e.preventDefault();
    }

    function onTouchEnd(e) {
      for (var i = 0; i < e.changedTouches.length; i++) {
        endDrag(e.changedTouches[i].identifier);
      }
    }

    function onMouseDown(e) {
      if (startDrag('mouse', e.clientX, e.clientY)) e.preventDefault();
    }

    function onMouseMove(e) {
      moveDrag('mouse', e.clientX, e.clientY);
    }

    function onMouseUp() {
      endDrag('mouse');
    }

    // Non-passive on purpose: preventDefault is the entire mechanism
    // for letting a grab win over a scroll. Torn down when rain ends.
    window.addEventListener('touchstart', onTouchStart, { passive: false });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    function teardownInput() {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    }

    /* ---------- drawing ---------- */

    function drawCoin(item) {
      var r = item.size / 2;
      // squash horizontally by the spin angle so it reads as a
      // tumbling disc rather than a flat dot
      var squash = Math.max(0.12, Math.abs(Math.cos(item.rot)));
      ctx.save();
      ctx.translate(item.x, item.y);
      ctx.scale(squash, 1);
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fillStyle = '#f2c34a';
      ctx.fill();
      ctx.lineWidth = Math.max(1, r * 0.16);
      ctx.strokeStyle = '#a8741a';
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.56, 0, Math.PI * 2);
      ctx.fillStyle = '#ffe08a';
      ctx.fill();
      ctx.restore();
    }

    function drawGem(item) {
      var s = item.size;
      ctx.save();
      ctx.translate(item.x, item.y);
      ctx.rotate(item.rot);

      if (item.cut === 'round') {
        // brilliant cut: flat table on top, tapering to a point
        ctx.beginPath();
        ctx.moveTo(-s * 0.3, -s * 0.42);
        ctx.lineTo(s * 0.3, -s * 0.42);
        ctx.lineTo(s * 0.5, -s * 0.12);
        ctx.lineTo(0, s * 0.6);
        ctx.lineTo(-s * 0.5, -s * 0.12);
        ctx.closePath();
        ctx.fillStyle = item.gem[0];
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(s * 0.3, -s * 0.42);
        ctx.lineTo(s * 0.5, -s * 0.12);
        ctx.lineTo(0, s * 0.6);
        ctx.closePath();
        ctx.fillStyle = item.gem[1];
        ctx.fill();
        // table highlight
        ctx.beginPath();
        ctx.moveTo(-s * 0.3, -s * 0.42);
        ctx.lineTo(s * 0.3, -s * 0.42);
        ctx.lineTo(s * 0.16, -s * 0.24);
        ctx.lineTo(-s * 0.16, -s * 0.24);
        ctx.closePath();
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(0, -s * 0.62);
        ctx.lineTo(s * 0.5, -s * 0.12);
        ctx.lineTo(0, s * 0.66);
        ctx.lineTo(-s * 0.5, -s * 0.12);
        ctx.closePath();
        ctx.fillStyle = item.gem[0];
        ctx.fill();
        // darker right-hand facet for a bit of depth
        ctx.beginPath();
        ctx.moveTo(0, -s * 0.62);
        ctx.lineTo(s * 0.5, -s * 0.12);
        ctx.lineTo(0, s * 0.66);
        ctx.closePath();
        ctx.fillStyle = item.gem[1];
        ctx.fill();
      }

      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(255,255,255,0.55)';
      ctx.stroke();
      ctx.restore();
    }

    function drawChest(item) {
      var s = item.size;
      ctx.save();
      ctx.translate(item.x, item.y);
      ctx.rotate(item.rot * 0.35); // chests tumble less than coins
      ctx.fillStyle = '#7b4a24';
      ctx.fillRect(-s / 2, -s * 0.14, s, s * 0.52);
      ctx.fillStyle = '#94592b';
      ctx.fillRect(-s / 2, -s * 0.44, s, s * 0.32);
      ctx.fillStyle = '#e8b93f';
      ctx.fillRect(-s / 2, -s * 0.18, s, s * 0.09);
      ctx.fillRect(-s * 0.08, -s * 0.22, s * 0.16, s * 0.24);
      ctx.restore();
    }

    function drawSword(item) {
      var s = item.size;
      ctx.save();
      ctx.translate(item.x, item.y);
      ctx.rotate(item.rot);

      // blade
      ctx.fillStyle = '#d9dee4';
      ctx.beginPath();
      ctx.moveTo(0, -s * 0.5);
      ctx.lineTo(s * 0.08, -s * 0.38);
      ctx.lineTo(s * 0.08, s * 0.08);
      ctx.lineTo(-s * 0.08, s * 0.08);
      ctx.lineTo(-s * 0.08, -s * 0.38);
      ctx.closePath();
      ctx.fill();

      // shaded half, so the blade catches light on one edge
      ctx.fillStyle = '#a6afb8';
      ctx.beginPath();
      ctx.moveTo(0, -s * 0.5);
      ctx.lineTo(s * 0.08, -s * 0.38);
      ctx.lineTo(s * 0.08, s * 0.08);
      ctx.lineTo(0, s * 0.08);
      ctx.closePath();
      ctx.fill();

      // crossguard
      ctx.fillStyle = '#e8b93f';
      ctx.fillRect(-s * 0.24, s * 0.08, s * 0.48, s * 0.08);

      // grip
      ctx.fillStyle = '#6b4327';
      ctx.fillRect(-s * 0.055, s * 0.16, s * 0.11, s * 0.26);

      // pommel
      ctx.fillStyle = '#e8b93f';
      ctx.beginPath();
      ctx.arc(0, s * 0.45, s * 0.075, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    }

    function drawAxe(item) {
      var s = item.size;
      ctx.save();
      ctx.translate(item.x, item.y);
      ctx.rotate(item.rot);

      // haft
      ctx.fillStyle = '#6b4327';
      ctx.fillRect(-s * 0.05, -s * 0.44, s * 0.1, s * 0.88);
      // haft binding
      ctx.fillStyle = '#4d2f1a';
      ctx.fillRect(-s * 0.05, s * 0.16, s * 0.1, s * 0.08);

      // main bit, sweeping right
      ctx.fillStyle = '#c6ccd3';
      ctx.beginPath();
      ctx.moveTo(s * 0.03, -s * 0.44);
      ctx.quadraticCurveTo(s * 0.44, -s * 0.38, s * 0.34, -s * 0.08);
      ctx.lineTo(s * 0.03, -s * 0.16);
      ctx.closePath();
      ctx.fill();

      // smaller back bit
      ctx.fillStyle = '#9aa4ad';
      ctx.beginPath();
      ctx.moveTo(-s * 0.03, -s * 0.44);
      ctx.quadraticCurveTo(-s * 0.3, -s * 0.38, -s * 0.24, -s * 0.12);
      ctx.lineTo(-s * 0.03, -s * 0.18);
      ctx.closePath();
      ctx.fill();

      ctx.restore();
    }

    function drawShape(item) {
      if (item.type === 'coin') {
        drawCoin(item);
      } else if (item.type === 'gem') {
        drawGem(item);
      } else if (item.type === 'sword') {
        drawSword(item);
      } else if (item.type === 'axe') {
        drawAxe(item);
      } else {
        drawChest(item);
      }
    }

    function drawItem(item) {
      var swollen = item.scale > 1.01;

      if (swollen) {
        // scale about the piece's own centre, so it grows in place
        // under the finger instead of sliding out from under it
        ctx.save();
        ctx.translate(item.x, item.y);
        ctx.scale(item.scale, item.scale);
        ctx.translate(-item.x, -item.y);
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 14;
        ctx.shadowOffsetY = 5;
      }

      drawShape(item);

      if (swollen) ctx.restore();
    }

    /* ---------- loop ---------- */

    var started = Date.now();
    var frame;

    function tick() {
      ctx.clearRect(0, 0, w, h);
      var alive = 0;
      var anyHeld = false;
      var heldItems = [];

      for (var i = 0; i < items.length; i++) {
        var item = items[i];

        // ease toward the target size in both directions, so it
        // swells on grab and settles back down after release
        var target = item.held ? GRAB_SCALE : 1;
        item.scale += (target - item.scale) * SCALE_EASE;

        if (item.held) {
          anyHeld = true;
          alive++;
          heldItems.push(item); // drawn last, on top of everything
          continue; // a held piece ignores gravity entirely
        }

        item.vy += 0.045; // gravity
        item.vx *= 0.99; // light air drag, so a fling settles
        item.sway += item.swaySpeed;
        item.y += item.vy;
        item.x += item.vx + Math.sin(item.sway) * item.swayAmt;
        item.rot += item.vr;
        item.vr *= 0.995;

        if (item.y < h + 90) alive++;
        if (item.y < -80 || item.y > h + 90) continue;
        if (item.x < -120 || item.x > w + 120) continue;

        drawItem(item);
      }

      for (var j = 0; j < heldItems.length; j++) drawItem(heldItems[j]);

      // Hard stop so a backgrounded tab can never leave this running,
      // but never yank a piece out of someone's fingers.
      var expired = Date.now() - started > 25000;
      if ((alive > 0 && !expired) || (anyHeld && !expired)) {
        frame = window.requestAnimationFrame(tick);
      } else {
        window.cancelAnimationFrame(frame);
        window.removeEventListener('resize', sizeCanvas);
        teardownInput();
        if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
        running = false;
      }
    }

    frame = window.requestAnimationFrame(tick);
  }

  window.ttLootRain = lootRain;

  window.ttLootRain.reset = function () {
    pagePlays = 0;
    lastRun = 0;
    var s = store();
    if (s) {
      try {
        s.removeItem(STORE_KEY);
      } catch (e) {
        /* no-op */
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return 'loot rain reset';
  };

  window.ttLootRain.plays = function () {
    return { used: getPlays(), max: MAX_PLAYS, scope: LIMIT_SCOPE };
  };
})();
